import httpRepo from '../httpRepo';
import { agentEndpoints } from '../apiConfig';
import { 
    getFirestore, 
    collection, 
    query, 
    where, 
    limit, 
    getDocs 
} from 'firebase/firestore';
import { Result } from '../../models/result';
import { StudentReport } from '../../models/reports/student_report';

export const getReports = async ({ schoolId, userToken }) => {
    return await httpRepo.get(agentEndpoints.getReports(schoolId), { userToken });
};

export const getReportItems = async ({ reportId, schoolId, userToken }) => {
    const url = agentEndpoints.getReportItems(reportId, schoolId);
    return await httpRepo.get(url, { userToken });
};

/**
 * Fetches a single student report from Firestore.
 * Queries collection: schools/{schoolId}/student_reports
 * Matches Flutter getStudentReport in user_repository.dart with Class, sec and academicYear filters
 * 
 * @param {Object} params - { schoolId, userId, admissionNo, Class, sec, academicYear }
 * @returns {Promise<Result>} Single StudentReport or empty result
 */
export const getStudentReport = async ({ schoolId, userId, admissionNo, Class, sec, academicYear } = {}) => {
    try {
        if (!schoolId) {
            return new Result({ message: 'schoolId is required to fetch student report' });
        }
        if (!userId && !admissionNo) {
            return new Result({ message: 'userId or admissionNo is required to fetch student report' });
        }

        const db = getFirestore();
        const reportsRef = collection(db, 'schools', schoolId, 'student_reports');
        const filterConstraints = [];

        if (Class !== undefined && Class !== null && String(Class).trim() !== '') {
            filterConstraints.push(where('Class', '==', isNaN(Number(Class)) ? Class : Number(Class)));
        }

        if (sec && String(sec).trim() !== '') {
            filterConstraints.push(where('sec', '==', String(sec).trim()));
        }

        if (academicYear && String(academicYear).trim() !== '') {
            filterConstraints.push(where('academicYear', '==', String(academicYear).trim()));
        }

        // 1. Primary lookup by userId if provided
        if (userId) {
            try {
                const q = query(reportsRef, where('userId', '==', String(userId)), ...filterConstraints, limit(1));
                const snap = await getDocs(q);
                if (!snap.empty) {
                    const data = snap.docs[0].data();
                    return new Result({ value: StudentReport.fromMap(data) });
                }
            } catch (err) {
                // Fallback to simple userId query if composite index not found
                const qSimple = query(reportsRef, where('userId', '==', String(userId)), limit(1));
                const snapSimple = await getDocs(qSimple);
                if (!snapSimple.empty) {
                    const data = snapSimple.docs[0].data();
                    return new Result({ value: StudentReport.fromMap(data) });
                }
            }
        }

        // 2. Secondary lookup by admissionNo if provided or if userId lookup was not found
        if (admissionNo) {
            const admStr = String(admissionNo).trim();
            try {
                const qAdm = query(reportsRef, where('admissionNo', '==', admStr), ...filterConstraints, limit(1));
                const snapAdm = await getDocs(qAdm);
                if (!snapAdm.empty) {
                    const data = snapAdm.docs[0].data();
                    return new Result({ value: StudentReport.fromMap(data) });
                }
            } catch (err) {
                const qAdmSimple = query(reportsRef, where('admissionNo', '==', admStr), limit(1));
                const snapAdmSimple = await getDocs(qAdmSimple);
                if (!snapAdmSimple.empty) {
                    const data = snapAdmSimple.docs[0].data();
                    return new Result({ value: StudentReport.fromMap(data) });
                }
            }

            // If not found directly, check if user exists to find userId
            const usersRef = collection(db, 'schools', schoolId, 'users');
            const qUser = query(usersRef, where('admissionNo', '==', admStr), where('type', '==', 1), limit(1));
            const snapUser = await getDocs(qUser);
            if (!snapUser.empty) {
                const targetUserId = snapUser.docs[0].id;
                const qByFoundUserId = query(reportsRef, where('userId', '==', targetUserId), limit(1));
                const snapByUserId = await getDocs(qByFoundUserId);
                if (!snapByUserId.empty) {
                    const data = snapByUserId.docs[0].data();
                    return new Result({ value: StudentReport.fromMap(data) });
                }
            }
        }

        return new Result({
            message: 'No report found for this student',
            value: null
        });

    } catch (ex) {
        console.error('[reportsRepo] Error in getStudentReport:', ex);
        return new Result({ message: ex.message || ex.toString() });
    }
};

/**
 * Fetches multiple student reports with optional filters.
 * Queries collection: schools/{schoolId}/student_reports
 * Matches Flutter getStudentReports in user_repository.dart
 * 
 * @param {Object} params - { schoolId, Class, sec, academicYear, userIds }
 * @returns {Promise<Result>} List of StudentReport
 */
export const getStudentReports = async ({ schoolId, Class, sec, academicYear, userIds } = {}) => {
    try {
        if (!schoolId) {
            return new Result({ message: 'schoolId is required to fetch student reports' });
        }

        const db = getFirestore();
        const reportsRef = collection(db, 'schools', schoolId, 'student_reports');
        const constraints = [];

        if (Class !== undefined && Class !== null && String(Class).trim() !== '') {
            constraints.push(where('Class', '==', isNaN(Number(Class)) ? Class : Number(Class)));
        }

        if (sec && String(sec).trim() !== '') {
            constraints.push(where('sec', '==', String(sec).trim()));
        }

        if (academicYear && String(academicYear).trim() !== '') {
            constraints.push(where('academicYear', '==', String(academicYear).trim()));
        }

        if (Array.isArray(userIds) && userIds.length > 0) {
            // Firestore 'in' query allows up to 30 elements
            const slicedUserIds = userIds.slice(0, 30);
            constraints.push(where('userId', 'in', slicedUserIds));
        }

        let snap;
        try {
            const q = constraints.length > 0 ? query(reportsRef, ...constraints) : reportsRef;
            snap = await getDocs(q);
        } catch (indexErr) {
            console.warn('[reportsRepo] Compound query failed, attempting without academicYear filter:', indexErr);
            // Fallback without academicYear filter in case composite index is not set
            const fallbackConstraints = constraints.filter(c => {
                return c._query && c._query.field && c._query.field.segments[0] !== 'academicYear';
            });
            const fallbackQ = fallbackConstraints.length > 0 ? query(reportsRef, ...fallbackConstraints) : reportsRef;
            snap = await getDocs(fallbackQ);
        }

        const reports = [];
        snap.forEach((doc) => {
            const data = doc.data();
            const report = StudentReport.fromMap(data);
            if (report) reports.push(report);
        });

        return new Result({ value: reports });

    } catch (ex) {
        console.error('[reportsRepo] Error in getStudentReports:', ex);
        return new Result({ message: ex.message || ex.toString() });
    }
};
